import React from 'react';
import { Separator } from './ui/separator';
import { Facebook, Instagram, Linkedin, Mail, Phone, MapPin } from 'lucide-react';

const Footer = () => {
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
    }
  };

  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-slate-900 text-white">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
          {/* Company Info */}
          <div className="lg:col-span-2">
            <div className="flex items-center mb-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_868e5e9c-7cb1-4485-87f4-7a4e3322b781/artifacts/lyr3d8vs_salinguntung_02.1b7e0472.svg" 
                alt="SalingUntung - Platform Digital Ekosistem Pariwisata Indonesia" 
                className="h-8 w-auto mr-3 filter brightness-0 invert"
              />
              <span className="text-xl font-bold">SalingUntung</span>
            </div>
            <p className="text-slate-300 mb-6 max-w-md">
              Platform Digital Ekosistem Pariwisata Indonesia yang menghubungkan driver wisata dengan merchant, 
              menciptakan ekosistem yang saling menguntungkan di seluruh Indonesia.
            </p>

            {/* Company Legal Information */}
            <div className="bg-slate-800 rounded-lg p-4 mb-6">
              <h4 className="text-sm font-semibold text-white mb-2">Informasi Perusahaan</h4>
              <div className="space-y-1 text-xs text-slate-300">
                <p><span className="font-medium">PT. Mari Digital Indonesia</span></p>
                <p>NPWP: 27.021.246.7-422.000</p>
                <p>NIB: 3009240075729</p>
                <p>Didirikan: 24 September 2024</p>
                <p className="pt-1 text-slate-400">Platform Digital Ekosistem Pariwisata</p>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center text-slate-300">
                <MapPin className="h-4 w-4 mr-2 flex-shrink-0" />
                <span className="text-sm">KATAPANG SQUARE NO.A5, JL.TERUSAN KOPO BLOK A7, Pangauban, Katapang, Kab. Bandung, Jawa Barat 40921</span>
              </div>
              <div className="flex items-center text-slate-300">
                <Phone className="h-4 w-4 mr-2 flex-shrink-0" />
                <span className="text-sm">+62 857-2134-7799</span>
              </div>
              <div className="flex items-center text-slate-300">
                <Mail className="h-4 w-4 mr-2 flex-shrink-0" />
                <span className="text-sm">maridigital.id@gmail.com</span>
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Menu Utama</h3>
            <ul className="space-y-3">
              {[
                { label: 'Home', id: 'hero' },
                { label: 'Untuk Driver', id: 'driver' },
                { label: 'Untuk Merchant', id: 'merchant' },
                { label: 'Cara Kerja', id: 'how-it-works' },
                { label: 'Tentang Kami', id: 'about' },
                { label: 'Kontak', id: 'contact' }
              ].map((item) => (
                <li key={item.id}>
                  <button
                    onClick={() => scrollToSection(item.id)}
                    className="text-slate-300 hover:text-white transition-colors duration-200 text-sm"
                  >
                    {item.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Layanan</h3>
            <ul className="space-y-3">
              <li>
                <a 
                  href="https://mitra.salinguntung.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-slate-300 hover:text-white transition-colors duration-200 text-sm"
                >
                  Registrasi Driver
                </a>
              </li>
              <li>
                <a 
                  href="https://merchant.salinguntung.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-slate-300 hover:text-white transition-colors duration-200 text-sm"
                >
                  Registrasi Merchant
                </a>
              </li>
              <li>
                <button
                  onClick={() => scrollToSection('contact')}
                  className="text-slate-300 hover:text-white transition-colors duration-200 text-sm"
                >
                  Bantuan & Support
                </button>
              </li>
              <li>
                <span className="text-slate-400 text-sm">FAQ (Segera Hadir)</span>
              </li>
              <li>
                <span className="text-slate-400 text-sm">Blog & News (Segera Hadir)</span>
              </li>
            </ul>
          </div>
        </div>

        <Separator className="bg-slate-700 my-8" />

        {/* Bottom Footer */}
        <div className="flex flex-col md:flex-row items-center justify-between">
          {/* Copyright */}
          <div className="text-slate-400 text-sm mb-4 md:mb-0">
            © {currentYear} SalingUntung.com - PT. Mari Digital Indonesia. All rights reserved.
            <div className="mt-1 text-xs">
              NPWP: 27.021.246.7-422.000 | NIB: 3009240075729
            </div>
          </div>

          {/* Social Media */}
          <div className="flex items-center space-x-4">
            <span className="text-slate-400 text-sm mr-2">Follow us:</span>
            <a 
              href="https://instagram.com/salinguntung" 
              target="_blank"
              rel="noopener noreferrer"
              className="text-slate-400 hover:text-[#2D9CDB] transition-colors duration-200"
              aria-label="Instagram SalingUntung"
            >
              <Instagram className="h-5 w-5" />
            </a>
            <a 
              href="https://facebook.com/salinguntung" 
              target="_blank"
              rel="noopener noreferrer"
              className="text-slate-400 hover:text-[#2D9CDB] transition-colors duration-200"
              aria-label="Facebook SalingUntung"
            >
              <Facebook className="h-5 w-5" />
            </a>
            <a 
              href="https://linkedin.com/company/salinguntung" 
              target="_blank"
              rel="noopener noreferrer"
              className="text-slate-400 hover:text-[#2D9CDB] transition-colors duration-200"
              aria-label="LinkedIn SalingUntung"
            ></a>
          </div>
        </div>

        {/* Legal Links */}
        <div className="mt-6 pt-6 border-t border-slate-700">
          <div className="flex flex-col sm:flex-row items-center justify-center space-y-2 sm:space-y-0 sm:space-x-6 text-sm text-slate-400">
            <span>Syarat & Ketentuan (Segera Hadir)</span>
            <span className="hidden sm:block">•</span>
            <span>Kebijakan Privasi (Segera Hadir)</span>
            <span className="hidden sm:block">•</span>
            <span>Cookie Policy (Segera Hadir)</span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;